import { useState, useEffect } from "react";
import axios from "axios";
import { useLang } from "../contexts/LangContext";
import { useAuth } from "../contexts/AuthContext";
import { Cpu, Plus, Search, Edit, Trash2, ArrowRightLeft, Clock, X, Download } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CLASS = {
    active: "badge-active", stock: "badge-stock",
    inactive: "badge-inactive"
};

const defaultForm = { imei: "", model: "", status: "stock", client_id: "", notes: "" };

export default function DevicesPage() {
    const { t } = useLang();
    const { user } = useAuth();
    const [devices, setDevices] = useState([]);
    const [clients, setClients] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [showModal, setShowModal] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [transferDevice, setTransferDevice] = useState(null);
    const [historyDevice, setHistoryDevice] = useState(null);
    const [history, setHistory] = useState([]);
    const [form, setForm] = useState(defaultForm);
    const [transferClientId, setTransferClientId] = useState("");
    const [transferNotes, setTransferNotes] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const canEdit = ["admin", "staff"].includes(user?.role);

    const fetchDevices = async () => {
        setLoading(true);
        try {
            const params = { limit: 100, skip: 0 };
            if (search) params.q = search;
            if (statusFilter && statusFilter !== "all") params.status = statusFilter;
            const { data } = await axios.get(`${API}/devices`, { params, withCredentials: true });
            setDevices(data.items);
            setTotal(data.total);
        } catch (e) { console.error(e); }
        setLoading(false);
    };

    const fetchClients = async () => {
        try {
            const { data } = await axios.get(`${API}/clients`, { params: { limit: 500 }, withCredentials: true });
            setClients(data.items);
        } catch (e) {}
    };

    useEffect(() => { fetchClients(); }, []);
    useEffect(() => {
        const t = setTimeout(fetchDevices, 300);
        return () => clearTimeout(t);
    }, [search, statusFilter]);

    const openAdd = () => { setEditItem(null); setForm(defaultForm); setError(""); setShowModal(true); };
    const openEdit = (item) => { setEditItem(item); setForm({ imei: item.imei, model: item.model, status: item.status, client_id: item.client_id || "", notes: item.notes || "" }); setError(""); setShowModal(true); };

    const openTransfer = (device) => {
        setTransferDevice(device);
        setTransferClientId(device.client_id || "");
        setTransferNotes("");
        setShowTransferModal(true);
    };

    const openHistory = async (device) => {
        setHistoryDevice(device);
        setShowHistoryModal(true);
        try {
            const { data } = await axios.get(`${API}/devices/${device.id}/history`, { withCredentials: true });
            setHistory(data);
        } catch (e) { setHistory([]); }
    };

    const handleSubmit = async (e) => {
        e.preventDefault(); setSaving(true); setError("");
        try {
            const payload = { ...form };
            if (!payload.client_id) delete payload.client_id;
            if (editItem) {
                await axios.put(`${API}/devices/${editItem.id}`, payload, { withCredentials: true });
            } else {
                await axios.post(`${API}/devices`, payload, { withCredentials: true });
            }
            setShowModal(false); fetchDevices();
        } catch (e) { setError(e.response?.data?.detail || t("error")); }
        setSaving(false);
    };

    const handleTransfer = async (e) => {
        e.preventDefault(); setSaving(true);
        try {
            await axios.post(`${API}/devices/${transferDevice.id}/transfer`, {
                to_client_id: transferClientId || null,
                notes: transferNotes
            }, { withCredentials: true });
            setShowTransferModal(false); fetchDevices();
        } catch (e) { alert(e.response?.data?.detail || t("error")); }
        setSaving(false);
    };

    const handleDelete = async (item) => {
        if (!window.confirm(t("confirm_delete"))) return;
        try {
            await axios.delete(`${API}/devices/${item.id}`, { withCredentials: true });
            fetchDevices();
        } catch (e) { alert(e.response?.data?.detail || t("error")); }
    };

    const f = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

    const ACTION_LABELS = { assigned: t("assigned"), transferred: t("transferred"), unassigned: t("unassigned") };

    return (
        <div className="p-6">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                        <Cpu size={20} className="text-[#E7212E]" /> {t("devices")}
                    </h1>
                    <p className="text-sm text-slate-500 mt-0.5">{total} pajisje gjithsej</p>
                </div>
                {canEdit && (
                    <div className="flex items-center gap-2">
                        <button onClick={async () => {
                            try {
                                const res = await axios.get(`${API}/export/devices.csv`, { withCredentials: true, responseType: "blob" });
                                const url = window.URL.createObjectURL(new Blob([res.data]));
                                const link = document.createElement("a"); link.href = url;
                                link.setAttribute("download", `pajisjet-${new Date().toISOString().substring(0, 10)}.csv`);
                                document.body.appendChild(link); link.click(); link.remove();
                            } catch { alert("Gabim gjatë eksportimit"); }
                        }} data-testid="export-devices-btn"
                            className="flex items-center gap-2 border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-medium px-3 py-2 rounded transition-colors">
                            <Download size={14} /> CSV
                        </button>
                        <button onClick={openAdd} data-testid="add-device-btn"
                            className="flex items-center gap-2 bg-[#E7212E] hover:bg-[#C91C26] text-white text-sm font-medium px-4 py-2 rounded transition-colors">
                            <Plus size={15} /> {t("add")}
                        </button>
                    </div>
                )}
            </div>

            <div className="flex gap-3 mb-4">
                <div className="relative flex-1 max-w-sm">
                    <Search size={14} className="absolute left-3 top-2.5 text-slate-400" />
                    <input
                        placeholder="Kërko IMEI, model, klient..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        data-testid="device-search"
                        className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E]"
                    />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-40" data-testid="device-status-filter">
                        <SelectValue placeholder={t("filter")} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t("all_statuses")}</SelectItem>
                        <SelectItem value="active">{t("active")}</SelectItem>
                        <SelectItem value="stock">{t("stock")}</SelectItem>
                        <SelectItem value="inactive">{t("inactive")}</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <div className="bg-white border border-slate-200 rounded overflow-hidden">
                <table className="w-full crm-table" data-testid="devices-table">
                    <thead>
                        <tr>
                            <th>IMEI</th>
                            <th>{t("model")}</th>
                            <th>{t("client")}</th>
                            <th>{t("status")}</th>
                            <th>{t("date")}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={6} className="text-center py-10 text-slate-400">{t("loading")}</td></tr>
                        ) : devices.length === 0 ? (
                            <tr><td colSpan={6} className="text-center py-10 text-slate-400">{t("no_data")}</td></tr>
                        ) : devices.map(d => (
                            <tr key={d.id} data-testid={`device-row-${d.id}`}>
                                <td>
                                    <span className="font-mono text-sm font-medium text-slate-900">{d.imei}</span>
                                </td>
                                <td className="text-sm">{d.model || "—"}</td>
                                <td className="text-sm font-medium">{d.client_name || <span className="text-slate-400 text-xs">{t("no_client")}</span>}</td>
                                <td>
                                    <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${STATUS_CLASS[d.status] || STATUS_CLASS.inactive}`}>
                                        {t(d.status)}
                                    </span>
                                </td>
                                <td className="text-xs text-slate-500">{d.created_at?.substring(0, 10)}</td>
                                <td>
                                    <div className="flex items-center gap-1">
                                        <button onClick={() => openHistory(d)} title="Historiku"
                                            className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors" data-testid={`history-device-${d.id}`}>
                                            <Clock size={14} />
                                        </button>
                                        {canEdit && (
                                            <>
                                                <button onClick={() => openTransfer(d)} title={t("transfer")}
                                                    className="p-1.5 rounded hover:bg-blue-50 text-slate-400 hover:text-blue-600 transition-colors" data-testid={`transfer-device-${d.id}`}>
                                                    <ArrowRightLeft size={14} />
                                                </button>
                                                <button onClick={() => openEdit(d)}
                                                    className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors" data-testid={`edit-device-${d.id}`}>
                                                    <Edit size={14} />
                                                </button>
                                                {user?.role === "admin" && (
                                                    <button onClick={() => handleDelete(d)}
                                                        className="p-1.5 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600 transition-colors" data-testid={`delete-device-${d.id}`}>
                                                        <Trash2 size={14} />
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Add/Edit Modal */}
            <Dialog open={showModal} onOpenChange={setShowModal}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>{editItem ? "Modifiko Pajisjen" : "Regjistro Pajisje të Re"}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-3 mt-2">
                        <div>
                            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">IMEI *</label>
                            <input required value={form.imei} onChange={e => f("imei", e.target.value)}
                                disabled={!!editItem}
                                data-testid="device-imei-input"
                                placeholder="353012345678901"
                                className="w-full px-3 py-2 border border-slate-300 rounded text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E] disabled:bg-slate-50 disabled:text-slate-400" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">Modeli</label>
                                <input value={form.model} onChange={e => f("model", e.target.value)}
                                    data-testid="device-model-input"
                                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E]" />
                            </div>
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">{t("status")}</label>
                                <Select value={form.status} onValueChange={v => f("status", v)}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="active">{t("active")}</SelectItem>
                                        <SelectItem value="stock">{t("stock")}</SelectItem>
                                        <SelectItem value="inactive">{t("inactive")}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">{t("client")}</label>
                            <Select value={form.client_id || "none"} onValueChange={v => f("client_id", v === "none" ? "" : v)}>
                                <SelectTrigger data-testid="device-client-select"><SelectValue placeholder={t("no_client")} /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">{t("no_client")}</SelectItem>
                                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name} {c.nipt ? `(${c.nipt})` : ""}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        {error && <p className="text-rose-600 text-sm bg-rose-50 p-2 rounded">{error}</p>}
                        <div className="flex justify-end gap-2 pt-2">
                            <button type="button" onClick={() => setShowModal(false)}
                                className="px-4 py-2 border border-slate-200 rounded text-sm text-slate-700 hover:bg-slate-50">{t("cancel")}</button>
                            <button type="submit" disabled={saving} data-testid="save-device-btn"
                                className="px-4 py-2 bg-[#E7212E] hover:bg-[#C91C26] text-white text-sm font-medium rounded disabled:opacity-60">
                                {saving ? "Duke ruajtur..." : t("save")}
                            </button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Transfer Modal */}
            <Dialog open={showTransferModal} onOpenChange={setShowTransferModal}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>{t("transfer_device")}</DialogTitle>
                    </DialogHeader>
                    {transferDevice && (
                        <form onSubmit={handleTransfer} className="space-y-3 mt-2">
                            <div className="bg-slate-50 rounded p-3">
                                <p className="text-xs text-slate-500 mb-1">IMEI</p>
                                <p className="font-mono font-medium text-slate-900">{transferDevice.imei}</p>
                                {transferDevice.client_name && (
                                    <p className="text-sm text-slate-500 mt-1">{t("from_client")}: <span className="font-medium">{transferDevice.client_name}</span></p>
                                )}
                            </div>
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">{t("to_client")}</label>
                                <Select value={transferClientId || "unassign"} onValueChange={v => setTransferClientId(v === "unassign" ? "" : v)}>
                                    <SelectTrigger data-testid="transfer-client-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="unassign">— {t("no_client")} (Hiq nga klienti)</SelectItem>
                                        {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name} {c.nipt ? `(${c.nipt})` : ""}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">{t("notes")}</label>
                                <input value={transferNotes} onChange={e => setTransferNotes(e.target.value)}
                                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E]" />
                            </div>
                            <div className="flex justify-end gap-2 pt-2">
                                <button type="button" onClick={() => setShowTransferModal(false)}
                                    className="px-4 py-2 border border-slate-200 rounded text-sm text-slate-700 hover:bg-slate-50">{t("cancel")}</button>
                                <button type="submit" disabled={saving} data-testid="confirm-transfer-btn"
                                    className="px-4 py-2 bg-[#E7212E] hover:bg-[#C91C26] text-white text-sm font-medium rounded disabled:opacity-60">
                                    {saving ? "Duke transferuar..." : t("transfer")}
                                </button>
                            </div>
                        </form>
                    )}
                </DialogContent>
            </Dialog>

            {/* History Modal */}
            <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{t("history")} — {historyDevice?.imei}</DialogTitle>
                    </DialogHeader>
                    <div className="mt-2 max-h-80 overflow-y-auto">
                        {history.length === 0 ? (
                            <p className="text-sm text-slate-400 text-center py-6">{t("no_data")}</p>
                        ) : (
                            <div className="space-y-2">
                                {history.map((h, i) => (
                                    <div key={h.id || i} className="flex items-start gap-3 p-3 bg-slate-50 rounded">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${h.action === "assigned" ? "badge-active" : h.action === "transferred" ? "badge-pending" : "badge-inactive"}`}>
                                                    {ACTION_LABELS[h.action] || h.action}
                                                </span>
                                                <span className="text-xs text-slate-400">{h.date?.substring(0, 10)}</span>
                                            </div>
                                            <p className="text-sm text-slate-600 mt-1">
                                                {h.from_client_name && <><span className="font-medium">{h.from_client_name}</span> → </>}
                                                <span className="font-medium">{h.to_client_name || t("no_client")}</span>
                                            </p>
                                            {h.notes && <p className="text-xs text-slate-400 mt-0.5">{h.notes}</p>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
